import { Dispatch, SetStateAction } from "react";
import { ServerConfig, fullHttpURL } from "./serverConfig";
import { ServerStatus, addLog, applySessionStatus, contestOngoing } from "./serverStatus";
import type { LiveSessionStatus } from "./generated/core-types";

type StatusUpdater = Dispatch<SetStateAction<ServerStatus>>;
async function responseError(response: Response): Promise<string> {
  const text = (await response.text()).trim();
  if (!text) return `Không thực hiện được yêu cầu (HTTP ${response.status}).`;
  try {
    const json = JSON.parse(text) as { error?: string; detail?: string; message?: string };
    return json.error || json.detail || json.message || text;
  } catch { return text; }
}
export async function getLiveStatus(config: ServerConfig, update: StatusUpdater): Promise<void> {
  try {
    const response = await fetch(fullHttpURL(config) + "/live/status");
    if (!response.ok) throw new Error("HTTP " + response.status);
    const status = await response.json() as LiveSessionStatus;
    update(prev => applySessionStatus(prev, status));
  } catch {
    update(prev => ({ ...prev, requestError: "Không đọc được trạng thái máy chủ." }));
  }
}
export async function toggleLive(config: ServerConfig, status: ServerStatus, liveId: string,
  videoId: string, inventorySize: number, update: StatusUpdater): Promise<void> {
  const start = status.status === "NO_LIVE";
  update(prev => ({ ...prev, requestPending: true, requestError: null }));
  try {
    const response = await fetch(fullHttpURL(config) + "/live", {
      method: start ? "POST" : "DELETE",
      body: start ? new URLSearchParams({ video: videoId, liveId, items: String(inventorySize),
        pageId: localStorage.getItem("activeFacebookPage") || "" }) : undefined,
    });
    if (!response.ok) {
      throw new Error(response.status === 409 ? "Đã có phiên đang chạy." : await responseError(response));
    }
    update(prev => addLog(prev, start ? "Đã gửi yêu cầu Start." : "Đã dừng tracking."));
    await getLiveStatus(config, update);
  } catch (error) {
    update(prev => ({ ...prev, requestError: error instanceof Error ? error.message : "Không kết nối được máy chủ." }));
  } finally {
    update(prev => ({ ...prev, requestPending: false }));
  }
}
export async function resumeLive(config: ServerConfig, update: StatusUpdater): Promise<void> {
  update(prev => ({ ...prev, requestPending: true, requestError: null }));
  try {
    const response = await fetch(fullHttpURL(config) + "/live/resume", { method: "POST" });
    if (!response.ok) throw new Error(await responseError(response));
    await getLiveStatus(config, update);
  } catch (error) {
    update(prev => ({ ...prev, requestError: error instanceof Error ? error.message : "Không nối lại được live." }));
  } finally {
    update(prev => ({ ...prev, requestPending: false }));
  }
}
export async function toggleContest(config: ServerConfig, status: ServerStatus, keyword: string, update: StatusUpdater): Promise<void> {
  try {
    const active = contestOngoing(status);
    const response = await fetch(fullHttpURL(config) + "/live/comments/validation/contest" +
      (active ? "" : "?keyword=" + encodeURIComponent(keyword)), { method: active ? "DELETE" : "POST" });
    if (!response.ok) throw new Error();
    update(prev => addLog(prev, active ? "Đã dừng cuộc thi" : "Đã bắt đầu cuộc thi"));
  } catch { update(prev => ({ ...prev, requestError: "Không cập nhật được cuộc thi." })); }
}
